import React, { useState } from 'react';
import CollapseCard from './CollapseCard';
import { clsErr, err } from '../validation';
import clsx from 'clsx';

/**
 * One fee-policy editor (inside <FeePoliciesForm>).
 *
 * rowIndex – index of the parent policy (for error keys)
 */
export default function FeePolicyEditor({
  rowIndex = 0,
  value = {},
  onChange,
  errors = {},
}) {
  const [open, setOpen] = useState(false);
  const toggle = () => setOpen((o) => !o);
  const rootKey = `fee_policies${rowIndex}`;
  const showCharge = value.prepayment > 0 || value.full_prepayment_required;

  /* shortcut updater */
  const upd = (patch) => onChange({ ...value, ...patch });

  /* ── cancellation rules ─────────────────────────────────── */
  const rules = value.cancellation_fee_rules || [];

  const addRule = () => {
    if (rules.length >= 5) return; // do nothing if limit reached
    upd({
      cancellation_fee_rules: [
        ...rules,
        { time_value: 24, time_unit: 'hours', amount: 0, type: 'percent' },
      ],
    });
  };


  const patchRule = (i, patch) =>
    upd({
      cancellation_fee_rules: rules.map((r, j) =>
        j === i ? { ...r, ...patch } : r,
      ),
    });

  const removeRule = (i) =>
    upd({ cancellation_fee_rules: rules.filter((_, j) => j !== i) });

  const localErr = rules.map((r) => ({
    badTime: !r.time_value || r.time_value <= 0,
    badAmt: r.amount === undefined || r.amount < 0,
  }));

  /* ── UI ─────────────────────────────────────────────────── */
  return (
    <CollapseCard title="Fee Policy" isOpen={open} onToggle={toggle}>
      {/* === Cancellation rules =============================== */}
      <div className="space-y-2 text-sm">
        <label className="font-medium block">Cancellation Fee Rules</label>

        {rules.map((r, i) => (
          <div
            key={i}
            className="space-y-2 rounded border p-2 shadow-sm bg-gray-50"
          >
            {/* time before */}
            <div className="flex items-center gap-2">
              <span className="w-28">Time before</span>
              <input
                type="number"
                min="1"
                className={clsx(
                  'w-24 rounded border px-2 py-0.5',
                (localErr[i].badTime ||
                  err(errors, `${rootKey}.cancel${i}.time`)) && 'border-red-500',
                )}
                value={r.time_value}
                onChange={(e) =>
                  patchRule(i, { time_value: Number(e.target.value) || 0 })
                }
              />
              <select
                value={r.time_unit}
                onChange={(e) =>
                  patchRule(i, { time_unit: e.target.value })
                }
                className="rounded border px-1 py-0.5"
              >
                <option value="hours">Hours</option>
                <option value="days">Days</option>
              </select>
            </div>
            {localErr[i].badTime && (
              <p className="text-xs text-red-600">Must be &gt; 0</p>
            )}
            {err(errors, `${rootKey}.cancel${i}.time`) && (
              <p className="text-xs text-red-600">
                {err(errors, `${rootKey}.cancel${i}.time`)}
              </p>
            )}
            {/* amount */}
            <div className="flex items-center gap-2">
              <span className="w-28">Amount</span>
              <input
                type="number"
                min="0"
                className={clsx(
                  'flex-1 rounded border px-2 py-0.5',
                  localErr[i].badAmt && 'border-red-500',
                )}
                value={r.amount}
                onChange={(e) =>
                  patchRule(i, { amount: parseFloat(e.target.value) })
                }
              />
              <select
                value={r.type}
                onChange={(e) => patchRule(i, { type: e.target.value })}
                className="rounded border px-1 py-0.5"
              >
                <option value="percent">%</option>
                <option value="fixed">Fixed</option>
              </select>
            </div>
            {localErr[i].badAmt && (
              <p className="text-xs text-red-600">Must be ≥ 0</p>
            )}

            <button
              type="button"
              onClick={() => removeRule(i)}
              className="text-xs text-red-500 underline"
            >
              Remove rule
            </button>
          </div>
        ))}

        {rules.length < 5 && (
          <button
            type="button"
            onClick={addRule}
            className="text-xs text-blue-600 underline"
          >
            + Add cancellation rule
          </button>
        )}

      </div>

      {/* === No-show fee ===================================== */}
      <div className="space-y-1 mt-4 text-sm">
        <label className="font-medium block">No-Show Fee</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            max={value.no_show_fee_type === 'percent' ? '100' : undefined}
            className="flex-1 rounded border px-2 py-0.5"
            value={value.no_show_fee || 0}
            onChange={(e) => {
              let v = Number(e.target.value) || 0;
              if (value.no_show_fee_type === 'percent' && v > 100) v = 100;
              upd({ no_show_fee: v });
            }}
          />
          <select
            value={value.no_show_fee_type}
            onChange={(e) => {
              const t = e.target.value;
              let v = value.no_show_fee || 0;
              if (t === 'percent' && v > 100) v = 100;
              upd({ no_show_fee_type: t, no_show_fee: v });
            }}
            className="rounded border px-1 py-0.5"
          >
            <option value="percent">%</option>
            <option value="fixed">Fixed</option>
          </select>
        </div>
      </div>

      {/* === No-show timeout ================================ */}
      <div className="space-y-1 mt-4 text-sm">
        <label className="font-medium block">No-Show Timeout</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="1"
            className={clsx(
              'flex-1 rounded border px-2 py-0.5',
              clsErr(errors, `${rootKey}.timeout`),
            )}
            value={value.no_show_timeout_value || 0}
            onChange={(e) =>
              upd({ no_show_timeout_value: Number(e.target.value) || 0 })
            }
          />
          <select
            value={value.no_show_timeout_unit}
            onChange={(e) =>
              upd({ no_show_timeout_unit: e.target.value })
            }
            className="rounded border px-1 py-0.5"
          >
            <option value="minutes">Minutes</option>
            <option value="hours">Hours</option>
          </select>
        </div>
        {err(errors, `${rootKey}.timeout`) && (
          <p className="text-xs text-red-600">
            {err(errors, `${rootKey}.timeout`)}
          </p>
        )}
      </div>

      {/* === Pre-payment ==================================== */}
      <div className="space-y-1 mt-4 text-sm">
        <label className="font-medium block">Pre-payment required</label>
        <div className="flex items-center gap-2">
          <input
            type="number"
            min="0"
            className="flex-1 rounded border px-2 py-0.5"
            value={value.prepayment || 0}
            onChange={(e) => {
              let v = Number(e.target.value) || 0;
              if (value.prepayment_type === 'percent' && v > 100) v = 100;
              upd({
                prepayment: v,
                ...(v === 0 && {
                  charge_before_value: 0,
                  charge_before_unit: 'days',
                }),
              });
            }}
          />
          <select
            value={value.prepayment_type}
            onChange={(e) => {
              const t = e.target.value;
              let v = value.prepayment || 0;
              if (t === 'percent' && v > 100) v = 100;
              upd({
                prepayment_type: t,
                prepayment: v,
                ...(v === 0 && {
                  charge_before_value: 0,        // hide + clear the field
                  charge_before_unit: 'days',
                }),
              });
            }}
            className="rounded border px-1 py-0.5"
          >
            <option value="percent">%</option>
            <option value="fixed">Fixed</option>
          </select>
        </div>
      </div>

      {/* === Charge before ================================== */}
      {showCharge && (
        <div className="space-y-1 mt-4 text-sm">
          <label className="font-medium block">Charge Before</label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0"
              className={clsx(
                'flex-1 rounded border px-2 py-0.5',
                clsErr(errors, `${rootKey}.charge_before`),
              )}
              value={value.charge_before_value || 0}
              onChange={(e) =>
                upd({ charge_before_value: Number(e.target.value) || 0 })
              }
            />
            <select
              value={value.charge_before_unit}
              onChange={(e) => upd({ charge_before_unit: e.target.value })}
              className="rounded border px-1 py-0.5"
            >
              <option value="hours">Hours</option>
              <option value="days">Days</option>
            </select>
          </div>
          {err(errors, `${rootKey}.charge_before`) && (
            <p className="text-xs text-red-600">
              {err(errors, `${rootKey}.charge_before`)}
            </p>
          )}
        </div>
      )}


      {/* === Full pre-payment ================================ */}
      <label className="flex items-center gap-2 mt-4 text-sm">
        <input
          type="checkbox"
          checked={value.full_prepayment_required || false}
          onChange={(e) =>
            upd({ full_prepayment_required: e.target.checked })
          }
        />
        Full pre-payment required
      </label>
    </CollapseCard>
  );
}
